import { Button } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import SliderScore from '@orientaction/components/SliderScore';
import { getFromLS, isAdmin } from '@orientaction/utils';
import dateFormat from 'dateformat';
import { omit } from 'lodash';
import { useState } from 'react';
import { apiServiceNative } from '../../../../../../utils/fetchNative';
import { SummaryCard, SummaryGrid, SummarySideCards } from '../components/SummarySection';
import { usePreferenceContentStyles } from './PreferenceContent.styles';
import { getPreferenceLabelColor, preferenceRankColors } from './preferencePresentation';

type PreferenceSummaryProps = {
  myResult: any;
  resultId?: number;
  onResultUpdated?: () => void;
  onSelect: (pref: any) => void;
};

const PreferenceSummary = ({
  myResult,
  resultId,
  onResultUpdated,
  onSelect,
}: PreferenceSummaryProps) => {
  const classes = usePreferenceContentStyles();
  const [isEdit, setIsEdit] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [newData, setNewData] = useState<Record<string, number>>({});
  const { consultant, updatedDate, initValue } = myResult || {};
  const preferences = myResult?.resultat || [];
  const user = JSON.parse(getFromLS('userInfos') || '{}');
  const canEdit = isAdmin(user) && !(updatedDate && !consultant?.id);

  const cancel = () => {
    setIsEdit(false);
    setIsCanceled(true);
    setNewData({});
  };

  const save = async () => {
    const today = new Date().toISOString().split('T')[0];
    const upperHierarchy = myResult?.user?.upper_hierarchy?.id;
    setIsEdit(false);
    try {
      if (!updatedDate) {
        await apiServiceNative.post('/api/preference-personality-results', {
          data: {
            ...omit(initValue, ['id', 'createdAt']),
            users_permissions_user: initValue?.users_permissions_user?.id,
            updatedDate: today,
          },
        });
      }
      await apiServiceNative.put(`/api/preference-personality-results/${resultId}`, {
        data: {
          ...newData,
          ...(upperHierarchy ? { consultant_id: upperHierarchy } : {}),
          updatedDate: today,
        },
      });
      onResultUpdated?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const validation = consultant?.firstname && consultant?.lastname && updatedDate && (
    <p className={classes.validation}>
      <CheckCircleOutlineIcon aria-hidden="true" />
      Profil validé par {consultant.firstname} {consultant.lastname} le{' '}
      {dateFormat(new Date(updatedDate), 'dd/mm/yyyy')}
    </p>
  );

  return (
    <SummaryGrid>
      <SummaryCard title="SYNTHÈSE DE VOS RÉSULTATS" beforeTitle={validation}>
        {canEdit && (
          <div className={classes.editActions}>
            {isEdit && <Button onClick={cancel}>Annuler</Button>}
            <Button onClick={() => (isEdit ? save() : setIsEdit(true))}>
              {isEdit ? 'Valider' : 'Modifier'}
            </Button>
          </div>
        )}
        {preferences.map((pref: any, index: number) => (
          <div
            key={`${pref.category}-${pref.key}`}
            className={classes.axis}
            style={{ color: preferenceRankColors[index] }}>
            <SliderScore
              pref={pref}
              isEdit={isEdit}
              isCanceled={isCanceled}
              restoreCanceled={setIsCanceled}
              handleNewData={(key: string, value: number) =>
                setNewData(previous => ({ ...previous, [key]: value }))
              }
            />
          </div>
        ))}
      </SummaryCard>
      <SummarySideCards>
        <SummaryCard title="VOS 5 PRÉFÉRENCES">
          <div className={classes.description}>
            <p>
              Voici vos cinq préférences dans votre personnalité profonde. La personnalité profonde
              correspond à votre manière de penser et de se comporter quand vous n’êtes soumis(e) à
              aucune contrainte.
            </p>
            <p>
              L’objectif est de trouver un métier et un environnement professionnel qui permettent
              d’exprimer vos préférences profondes. Bref, qui vous permettent d’être vous-même. Cela
              est aussi vrai dans votre vie personnelle.
            </p>
          </div>
          <div className={classes.chips}>
            {preferences.map((pref: any, index: number) => (
              <Button
                key={pref.category}
                variant="contained"
                disableElevation={true}
                style={{
                  background: preferenceRankColors[index],
                  color: getPreferenceLabelColor(index),
                }}
                onClick={() => onSelect(pref)}>
                {pref.label}
              </Button>
            ))}
          </div>
          <p className={classes.hint}>Cliquez sur une caractéristique pour en savoir + !</p>
        </SummaryCard>
      </SummarySideCards>
    </SummaryGrid>
  );
};

export default PreferenceSummary;
